import { z } from 'zod';
import { PlayerIdSchema } from '../ids';
import { TimestampMsSchema } from '../time';

export const DISPLAY_NAME_MAX_LENGTH = 32;

/**
 * A deleted character's name wherever its id still shows (CHAR-DELETE, CD-8 a)): never a name one
 * could choose (names are letters only), so the client words it «a forgotten traveller».
 */
export const FORGOTTEN_NAME = '~';

/** Whether a name is a deleted character's (CD-8). */
export const isForgottenName = (name: string): boolean => name === FORGOTTEN_NAME;

export const PlayerSchema = z.object({
  id: PlayerIdSchema,
  displayName: z.string().min(1).max(DISPLAY_NAME_MAX_LENGTH),
  /** Bot players are regular players driven by the scripted controller; no extra privileges. */
  isBot: z.boolean(),
  createdAt: TimestampMsSchema,
  /** Where it comes from (CHAR-ORIGIN, OI-1..5): one of the config's `origins`; absent from before. */
  origin: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .max(32)
    .optional(),
  /**
   * When its deletion's grace ends (CHAR-DELETE, CD-1), game time: it cannot be played meanwhile and
   * its owner may cancel it. Absent when none was asked; only its account's list carries it.
   */
  deleteAt: TimestampMsSchema.optional(),
});
export type Player = z.infer<typeof PlayerSchema>;

export const CHARACTER_NAME_MIN_LENGTH = 3;
export const CHARACTER_NAME_MAX_LENGTH = 20;

/**
 * Letters (any script, with their accents) in words joined by single spaces, apostrophes or
 * hyphens: "Aria", "Bram de Lis", "O'Neill", "Ana-Lucía". No digits or symbols, so names read as
 * names in the world.
 */
const CHARACTER_NAME_PATTERN = /^\p{L}\p{M}*(?:[ '-]?\p{L}\p{M}*)*$/u;

/** Canonical form of a typed name: NFC, trimmed, inner whitespace collapsed to one space. */
export const normalizeCharacterName = (name: string): string =>
  name.normalize('NFC').trim().replace(/\s+/gu, ' ');

/** Uniqueness key of a name: names differing only in case are the same name. */
export const characterNameKey = (name: string): string =>
  normalizeCharacterName(name).toLowerCase();

/** Name of a new character, validated after normalization (ADR 0029). */
export const CharacterNameSchema = z
  .string()
  .max(CHARACTER_NAME_MAX_LENGTH * 4)
  .transform(normalizeCharacterName)
  .pipe(
    z
      .string()
      .min(CHARACTER_NAME_MIN_LENGTH)
      .max(CHARACTER_NAME_MAX_LENGTH)
      .regex(CHARACTER_NAME_PATTERN),
  );
